/**
 * Pre-submit sales tax for the school checkout summary. Pure + testable
 * (no React, no `@/api` runtime imports) — mirrors `cart.ts`.
 *
 * Mirrors the backend money math exactly so the summary row equals what
 * submit will charge:
 *
 *   - The RATE is the school's `tax.effectiveTaxRateBps` from the
 *     `/my-schools` read — the rate the SCHOOL_FORECAST submit itself
 *     applies (0 today: school-side submits are never taxed at order
 *     time; see the server's `resolveSchoolOrderTaxInfo`). It is NOT
 *     the campus rate — FAMILY_DIRECT orders tax at that, school bulk
 *     orders do not.
 *   - Rounding is PER LINE, `Math.round(base × rate / 100000)` — the
 *     shared 0.001% tax-rate storage unit (`computeLineTaxCents`,
 *     @ordo/money's tax-rate helpers), summed over lines. One cart
 *     line becomes one OrderItem at submit, so client lines and server
 *     lines round at the same granularity and the sum can't drift.
 *   - School forecast lines carry no sponsorship, so the per-line base
 *     is the full line subtotal (`unitPriceCents × quantity`). If
 *     school-side tax ever activates alongside early-payment discounts,
 *     the base must subtract the line discount first (the
 *     `computeLineTaxCents` convention) — revisit then.
 *   - Returns `null` when NO line carries a price, mirroring
 *     `subtotalCents`, so the Tax row renders "—" alongside an
 *     unpriced subtotal instead of a misleading $0.00.
 */

import type { SchoolCartLine } from '@/lib/school-orders/cart'

/**
 * Per-line tax in cents: `Math.round(base × rate / 100000)`; 0 for
 * rate ≤ 0. The rate is in the shared 0.001% storage unit — mirror of
 * `TAX_RATE_STORAGE_DENOMINATOR` in @ordo/money (kept inline: this file
 * is a dependency-free pure mirror of the server math).
 */
export function lineTaxCents(lineSubtotalCents: number, taxRateBps: number): number {
  if (taxRateBps <= 0) {
    return 0
  }
  return Math.round((lineSubtotalCents * taxRateBps) / 100000)
}

/**
 * Pre-submit tax in cents across the cart — per-line rounding summed
 * over priced lines. `null` when no line has a price (mirror of
 * `subtotalCents`'s null contract).
 */
export function schoolOrderTaxCents(lines: SchoolCartLine[], effectiveTaxRateBps: number): number | null {
  let total = 0
  let priced = false
  for (const l of lines) {
    if (l.unitPriceCents != null) {
      total += lineTaxCents(l.unitPriceCents * l.quantity, effectiveTaxRateBps)
      priced = true
    }
  }
  return priced ? total : null
}

/**
 * ESTIMATED pre-submit tax for the school checkout summary.
 *
 * Distinct from `schoolOrderTaxCents` above, and the distinction is the whole
 * point:
 *
 *   - `schoolOrderTaxCents(lines, effectiveTaxRateBps)` is what SUBMIT
 *     charges — 0 for school bulk orders, because school tax is settled at
 *     invoicing. It is what the funds gate must keep using
 *     (`schoolOrderExpectedDebitCents`), or checkout would block on money the
 *     submit never debits.
 *   - `schoolOrderEstimatedTaxCents(lines, campusTaxRateBps)` is what the
 *     INVOICE is expected to add, at the campus's current rate. It is display
 *     only.
 *
 * Showing $0.00 was misleading — the school IS billed campus-rate sales tax,
 * just later — so the row shows this estimate and says so. The invoice
 * resolves tax per line against the rate in force on the service date and any
 * governed exemption, so this can differ; the UI labels it "Est." and
 * explains that in a tooltip.
 *
 * Same per-line rounding as everything else, so the estimate cannot drift
 * from the invoice by a rounding convention.
 *
 * Takes the whole `school.tax` object rather than a bare rate on purpose: the
 * org exemption has to be applied at EVERY surface that shows the estimate,
 * and a bare-rate signature let all three call sites drop it silently.
 *
 * Three outcomes, and the difference between the last two matters:
 *
 *   - EXEMPT org ⇒ 0. The invoice writer returns zero outright when the
 *     account's owner org is `taxExempt` (`computeAccountInvoiceSalesTax`),
 *     so this is a KNOWN zero and renders as `$0.00`. Most K-12 districts
 *     are exempt, so without this the common case reads an inflated Total
 *     for an invoice guaranteed to add nothing.
 *   - UNCONFIGURED campus rate (null) ⇒ null, so the row renders "—". The
 *     invoice will use whatever rate ops eventually sets; claiming `$0.00`
 *     here would reproduce the exact misleading zero this row exists to
 *     remove.
 *   - A real rate (including a genuine 0) ⇒ the computed estimate.
 */
export function schoolOrderEstimatedTaxCents(
  lines: SchoolCartLine[],
  tax: { campusTaxRateBps: number | null | undefined; orgTaxExempt?: boolean },
): number | null {
  if (tax.orgTaxExempt) {
    return schoolOrderTaxCents(lines, 0)
  }
  if (tax.campusTaxRateBps === null || tax.campusTaxRateBps === undefined) {
    return null
  }
  return schoolOrderTaxCents(lines, tax.campusTaxRateBps)
}
